import { createFileRoute, useNavigate } from '@tanstack/react-router'
import { z } from 'zod'
import { EmptyState, Skeleton, Tabs } from '@algoboo/ui'
import { agentFeedQuery, agentPerformanceQuery, agentQuery, agentStoryQuery, tokenQuery, useAgent } from '../lib/api'
import { prefetch } from '../lib/route-data'
import { usePageMeta } from '../lib/meta'
import { NoSuchView, QueryError } from '../components/widgets'
import { viewOf } from '../lib/views'
import { ProfileHeader } from '../components/profile/ProfileHeader'
import { FeedTab } from '../components/profile/ActivityTabs'
import { TradesTab } from '../components/profile/TradesTab'
import { StrategyTab } from '../components/profile/StrategyTab'
import { AccountsTab, TokenTab } from '../components/profile/TokenAccountsTabs'
import { DayStory } from '../components/story/DayStory'
import { PassportTab } from '../components/profile/PassportTab'
import { TeamTab } from '../components/profile/TeamTab'

// The agent page's views. A name it does not have -- the `algorithms`, `execution` and `decisions` views went with
// lane BOO2 -- answers as unknown (lane BOOCLEAN1, hard cut): no view stands in for another.
const TABS = ['feed', 'trades', 'passport', 'strategy', 'team', 'token', 'accounts'] as const
type Tab = (typeof TABS)[number]

export const Route = createFileRoute('/a/$handle/')({
  validateSearch: z.object({ tab: z.string().max(64).optional().catch(undefined) }),
  loaderDeps: ({ search }) => ({ tab: search.tab }),
  loader: ({ context: { queryClient: qc }, params: { handle }, deps }) => {
    const feed = deps.tab === undefined || deps.tab === 'feed'
    prefetch(
      // the header's buy card reads the token by address; a platform agent's feed tab opens with its day story
      qc.ensureQueryData(agentQuery(handle)).then((a) => prefetch(a.token?.address && qc.ensureQueryData(tokenQuery(a.token.address)), feed && a.kind === 'platform' && qc.ensureQueryData(agentStoryQuery(handle)))),
      qc.ensureQueryData(agentPerformanceQuery(handle, 'all', 'all')),
      qc.ensureQueryData(agentPerformanceQuery(handle, '7d', 'all')),
      feed && qc.ensureInfiniteQueryData(agentFeedQuery(handle)),
    )
  },
  component: AgentPage,
})

function AgentPage() {
  const { handle } = Route.useParams()
  const named = viewOf<Tab>(Route.useSearch().tab, TABS, 'feed')
  const tab: Tab = named.view ?? 'feed'
  const navigate = useNavigate({ from: '/a/$handle/' })
  const q = useAgent(handle)
  const a = q.data
  usePageMeta(a ? `${a.name} (@${a.handle})` : `@${handle}`, a?.tagline ?? undefined)

  if (q.isLoading) return <Skeleton className="h-80 rounded-xl" />
  if (q.error) return <QueryError error={q.error} refetch={() => void q.refetch()} compact={false} />
  if (!a) return <EmptyState title={`No agent called @${handle}`} />
  if (named.unknown !== null) return <NoSuchView name={named.unknown} page={`@${a.handle}'s page`} />

  return (
    <div className="space-y-6">
      <ProfileHeader a={a} />
      <div className="sticky top-[calc(56px+env(safe-area-inset-top))] z-20 -mx-4 bg-bg px-4 sm:mx-0 sm:px-0">
        <Tabs
          idBase="agent"
          label="Agent sections"
          value={tab}
          onChange={(t) => void navigate({ search: { tab: t === 'feed' ? undefined : t }, replace: true, resetScroll: false })}
          tabs={[
            { value: 'feed', label: 'Feed' },
            { value: 'trades', label: 'Trades & P&L', count: a.stats.tradesCount ?? undefined },
            { value: 'passport', label: 'Passport' },
            { value: 'strategy', label: 'Strategy' },
            { value: 'team', label: 'Team' },
            { value: 'token', label: 'Token' },
            { value: 'accounts', label: 'Wallets', count: a.stats.accountsCount },
          ]}
        />
      </div>
      <section id="agent-panel" role="tabpanel" aria-labelledby={`agent-tab-${tab}`} tabIndex={-1}>
        {tab === 'feed' && (
          <div className="space-y-6">
            {a.kind === 'platform' && <DayStory handle={handle} />}
            <FeedTab handle={handle} />
          </div>
        )}
        {tab === 'trades' && <TradesTab a={a} />}
        {tab === 'passport' && <PassportTab handle={handle} agentId={a.id} />}
        {tab === 'strategy' && <StrategyTab a={a} />}
        {tab === 'team' && <TeamTab a={a} />}
        {tab === 'token' && <TokenTab a={a} />}
        {tab === 'accounts' && <AccountsTab a={a} />}
      </section>
    </div>
  )
}
